import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BadgeCheck, Building2, Search } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { CtaBand } from '../components/page/CtaBand';
import { Reveal } from '../components/ui/Reveal';
import { Badge } from '../components/ui/Badge';
import { CASE_STUDIES } from '../components/caseStudiesData';
const SEO = {
  title: 'Case Studies | Brands & Marketing Agencies Working With Talent Agencies | Zano',
  description: "See how brands and marketing agencies used Zano's live talent agency roster database to connect with represented creators, launch high-value campaigns, and receive faster responses from talent agencies.",
  keywords: 'influencer marketing case studies, talent agency campaigns, represented creators, influencer sourcing, talent agency partnerships, creator campaigns',
  ogTitle: 'Customer Success Stories | Zano Case Studies',
  ogDescription: 'See how brands, marketing agencies, and talent agencies use Zano to discover creators, manage partnerships, and grow influencer campaigns.'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const CaseStudies = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <PageHeader eyebrow="Case Studies" title="Teams moving from discovery to activation" subtitle="How brands and marketing agencies use Zano to source and manage talent agency relationships at scale." data-id="element-2459" data-mp="qfxang9r0ij8" />

      <section aria-labelledby="positioning-banner-title" className="mx-auto max-w-[1584px] px-4 pb-10 sm:px-6 lg:px-8" data-id="element-2460" data-mp="zgv7nelrrpsb">
        <Reveal data-id="element-2461" data-mp="318hxvdxu7gnq">
          <div className="relative overflow-hidden rounded-3xl bg-zano-charcoal px-6 py-8 sm:px-10 sm:py-10 xl:aspect-[4/1] xl:px-14 xl:py-7" data-id="element-2462" data-mp="317zvdzkl067d">
            <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/3 rounded-full border-[22px] border-zano-pink/20" data-id="element-2463" data-mp="2tqjw5vlwd9v2" />
            <div className="pointer-events-none absolute bottom-0 left-0 h-44 w-44 -translate-x-1/2 translate-y-1/2 rounded-full border-[18px] border-white/5" data-id="element-2464" data-mp="2k0h35verzvwq" />
            <div className="pointer-events-none absolute bottom-0 left-[30%] hidden h-[58%] w-px bg-white/10 xl:block" data-id="element-2465" data-mp="3co4h4rljjoae" />

            <div className="relative grid h-full items-center gap-7 xl:grid-cols-[480px_minmax(0,1fr)_minmax(390px,0.82fr)] xl:gap-7" data-id="element-2466" data-mp="37oi2qetz8200">
              <div className="hidden self-stretch xl:block" aria-hidden="true" data-id="element-2467" data-mp="33lvaicte8f2a" />

              <div className="xl:pl-1" data-id="element-2468" data-mp="2yqo9xdvf7qhl">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-zano-pink" data-id="element-2469" data-mp="1s8jbcf8kwkmg">
                  Your agency partner network
                </p>
                <h2 id="positioning-banner-title" className="max-w-[28rem] text-3xl font-bold leading-[1.08] text-white sm:text-4xl" data-id="element-2470" data-mp="3b5uplcorhyzd">
                  Keep partner rosters current.{' '}
                  <span className="text-zano-pink" data-id="element-2471" data-mp="326hgil4jvcf6">
                    Discover new agencies at scale.
                  </span>
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-gray-300 sm:text-base" data-id="element-2472" data-mp="3twcipidiawl9">
                  Stay up to date with the live rosters of agencies you already
                  work with, then discover vetted new talent agency partners in
                  the same place.
                </p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-300" data-id="element-2473" data-mp="3puijleiym0n7">
                  <span className="inline-flex items-center gap-1.5" data-id="element-2474" data-mp="29ginyfr5cx5r">
                    <BadgeCheck size={15} className="text-zano-pink" data-id="element-2475" data-mp="29f7m4lh9hzha" />
                    Live partner rosters
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-2476" data-mp="25ybfd7s7hiks">
                    <Building2 size={15} className="text-zano-pink" data-id="element-2477" data-mp="260o361knan5r" />
                    400+ vetted agencies
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white p-3 text-left shadow-2xl" aria-label="Example of a live agency roster search" data-id="element-2478" data-mp="2fw92tt28rbcc">
                <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2" data-id="element-2479" data-mp="1n8mwid1mpsls">
                  <div className="flex gap-1" data-id="element-2480" data-mp="1ialg5zvl87e0">
                    <span className="h-2 w-2 rounded-full bg-rose-300" data-id="element-2481" data-mp="1wxn069r2tc6b" />
                    <span className="h-2 w-2 rounded-full bg-amber-300" data-id="element-2482" data-mp="1wzzsngugn4xm" />
                    <span className="h-2 w-2 rounded-full bg-emerald-300" data-id="element-2483" data-mp="1s5lrsyx145lk" />
                  </div>
                  <span className="ml-1 text-[11px] font-semibold text-gray-500" data-id="element-2484" data-mp="14g17ocp532ap">
                    Live Roster Database
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-700" data-id="element-2485" data-mp="2zksaaedpgxnr">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-2486" data-mp="34iafbtolqsqh" />
                    Updated live
                  </span>
                </div>

                <div className="flex items-center gap-2 rounded-lg border border-zano-pink/30 bg-zano-blush/50 px-3 py-1.5" data-id="element-2487" data-mp="3dcoyi7c96nev">
                  <Search size={15} className="shrink-0 text-zano-pink" data-id="element-2488" data-mp="2gn7r9nxmgtb2" />
                  <span className="truncate text-xs font-medium text-gray-700" data-id="element-2489" data-mp="2lk7n760rcfog">
                    Search represented creators
                  </span>
                  <span className="ml-auto hidden rounded-md bg-white px-2 py-1 text-[10px] font-medium text-gray-500 sm:inline" data-id="element-2490" data-mp="2pop7r7exyx0a">
                    Filters
                  </span>
                </div>

                <div className="mt-2 space-y-1.5" data-id="element-2491" data-mp="4yq0z19mutz9">
                  {[{
                  name: 'Clicks Talent',
                  logo: 'https://cdn.magicpatterns.com/uploads/oKbracun8P5MXQXuDPFXrA/clicks_talent_logo.webp'
                }, {
                  name: 'United Talent Agency',
                  logo: 'https://cdn.magicpatterns.com/uploads/iMEkKfrRdejvDfRHdG5tcE/uta.png'
                }, {
                  name: 'Famfluence',
                  logo: 'https://cdn.magicpatterns.com/uploads/iq5ZMVZ2aiG8SYPyU7XxgR/famfluence_logo.png'
                }].map(agency => <div key={agency.name} className="flex min-w-0 items-center gap-3 rounded-xl border border-gray-100 px-3 py-1.5" data-id="element-2492" data-mp="3ng40kohc13x9">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white p-1" data-id="element-2493" data-mp="1loqs5pg5odwg">
                        <img src={agency.logo} alt={`${agency.name} logo`} className="h-full w-full object-contain" data-id="element-2494" data-mp="1ho6zay2m7wg4" />
                      </div>
                      <span className="truncate text-xs font-semibold text-gray-800" data-id="element-2495" data-mp="24zbipdvhbt68">
                        {agency.name}
                      </span>
                      <BadgeCheck size={14} className="ml-auto shrink-0 text-emerald-500" data-id="element-2496" data-mp="1w45doh419f4r" />
                    </div>)}
                </div>

                <p className="mt-2 text-center text-[10px] leading-relaxed text-gray-500" data-id="element-2497" data-mp="1m8akis26o1nq">
                  Agency-owned data, not scraped creator profiles.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16" data-id="element-2498" data-mp="32mk8vm6fdqek">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2499" data-mp="37klry73uppq1">
          {CASE_STUDIES.map((c, i) => <Reveal key={c.slug} delay={i % 2 * 0.1} data-id="element-2500" data-mp="3hpbzqtlxro26" data-mpi={i}>
              <Link to={`/case-studies/${c.slug}`} className="group flex flex-col h-full bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-float hover:-translate-y-1 transition-all duration-300" data-id="element-2501" data-mp="3cp66j900pthz">
                <div className={`h-40 w-full flex items-center justify-center p-8 ${c.logoBg}`} data-id="element-2502" data-mp="3lnriumewccab">
                  <img src={c.logo} alt={`${c.brand} logo`} className="max-h-full max-w-[180px] object-contain transition-transform duration-500 group-hover:scale-105" data-id="element-2503" data-mp="3vfxjekj7s214" />
                </div>
                <div className="p-8 flex flex-col flex-1" data-id="element-2504" data-mp="96cs6tzf10mr">
                  <div className="flex flex-wrap gap-2 mb-4" data-id="element-2505" data-mp="2f2gyoiimwmo7">
                    {c.tags.map(tag => <Badge key={tag} className="bg-blue-50 border border-blue-200 text-blue-700" data-id="element-2506" data-mp="2b15c6lv24hk8">
                        {tag}
                      </Badge>)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-zano-pink transition-colors" data-id="element-2507" data-mp="33a5fxg9tmy0s">
                    {c.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1" data-id="element-2508" data-mp="3vzcl2y3s6lv0">
                    {c.subtitle}
                  </p>
                  <div className="flex items-center text-sm font-bold text-zano-pink mt-auto" data-id="element-2509" data-mp="3r1ufnhoz2orr">
                    Read Case Study
                    <ArrowUpRight size={16} className="ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-2510" data-mp="3m7gd8vbw7hdl" />
                  </div>
                </div>
              </Link>
            </Reveal>)}
        </div>
      </section>

      <CtaBand title="Be the next team to ditch the spreadsheets" subtitle="Start a free trial today and access the elite layer of talent representation." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} data-id="element-2511" data-mp="l5nrebk77blo" />
    </>;
};